import type { ReplayPayload } from "../types";

/** Field heat layers: food greens, hazard reds, grief violet; extras cycle cool hues. */
const FIELD_COLORS: Record<string, [number, number, number]> = {
  food: [46, 160, 67],
  hazard: [218, 54, 51],
  grief: [163, 113, 247],
};
const EXTRA_FIELD_COLORS: [number, number, number][] = [
  [47, 129, 247],
  [57, 197, 207],
  [219, 154, 4],
];

export function fieldColor(name: string, index: number): [number, number, number] {
  return FIELD_COLORS[name] ?? EXTRA_FIELD_COLORS[index % EXTRA_FIELD_COLORS.length];
}

// The same deterministic per-agent jitter the GIF renderer uses, so co-located dots
// stay distinguishable (see saga/harness/visualize.py).
export const jitterX = (agent: number): number => (((agent * 7) % 5) - 2) * 0.09;
export const jitterY = (agent: number): number => (((agent * 11) % 5) - 2) * 0.09;

// World-component markers reuse the replay's existing hues: structures take the
// ember of the trail/focus ring (#e8a33d — the settlement's mark on the world),
// items the teal from the extra-field cycle.
const STRUCTURE_RGB: [number, number, number] = [232, 163, 61];
const ITEM_RGB: [number, number, number] = [57, 197, 207];

const clamp01 = (value: number): number => Math.max(0, Math.min(1, value));

export type WorldMarker =
  | { kind: "structure"; node: number; x: number; y: number; filled: boolean; opacity: number }
  | { kind: "item"; node: number; x: number; y: number; scale: number };

/** The frame's world-component markers, in canvas coordinates.
 *
 *  Kinds come from the payload's `world_components` manifest — never from entry
 *  shapes — so a payload recorded before world components existed (no manifest,
 *  no per-frame `world`) yields no markers and renders exactly as before.
 *  Structures under construction (condition 0) are outlines whose opacity scales
 *  with build progress; built ones are filled with opacity = condition. Items
 *  collapse to one diamond per node, scaled by entry count (capped at 3×). */
export function worldMarkers(
  replay: ReplayPayload,
  frameIndex: number,
  cell: number,
): WorldMarker[] {
  const frame = replay.frames[frameIndex];
  const manifest = replay.world_components;
  if (!frame?.world || !manifest) return [];
  const width = replay.grid.width;
  const markers: WorldMarker[] = [];
  for (const { name, kind } of manifest) {
    const snapshot = frame.world[name];
    if (!snapshot) continue;
    // Sorted node order keeps the draw order (and any overlap) deterministic.
    const nodes = Object.keys(snapshot)
      .map(Number)
      .filter(Number.isFinite)
      .sort((a, b) => a - b);
    for (const node of nodes) {
      const entries = snapshot[String(node)];
      if (!entries?.length) continue;
      const x = ((node % width) + 0.5) * cell;
      const y = (Math.floor(node / width) + 0.5) * cell;
      if (kind === "structures") {
        entries.forEach((entry, index) => {
          // Nudge co-located structures apart, the way agent dots jitter.
          const offset = (index - (entries.length - 1) / 2) * cell * 0.16;
          const filled = entry.condition > 0;
          markers.push({
            kind: "structure",
            node,
            x: x + offset,
            y: y + offset,
            filled,
            opacity: filled
              ? clamp01(entry.condition)
              : 0.2 + 0.8 * clamp01(entry.progress ?? 0),
          });
        });
      } else if (kind === "items") {
        markers.push({ kind: "item", node, x, y, scale: Math.min(3, Math.sqrt(entries.length)) });
      }
    }
  }
  return markers;
}

/** Squares for structures (outline → filled as they're built), diamonds for items. */
function drawWorldMarkers(
  ctx: CanvasRenderingContext2D,
  markers: WorldMarker[],
  cell: number,
): void {
  const half = cell * 0.3;
  for (const marker of markers) {
    if (marker.kind === "structure") {
      const [r, g, b] = STRUCTURE_RGB;
      if (marker.filled) {
        ctx.fillStyle = `rgba(${r},${g},${b},${marker.opacity.toFixed(3)})`;
        ctx.fillRect(marker.x - half, marker.y - half, half * 2, half * 2);
      } else {
        ctx.strokeStyle = `rgba(${r},${g},${b},${marker.opacity.toFixed(3)})`;
        ctx.lineWidth = 1.5;
        ctx.strokeRect(marker.x - half, marker.y - half, half * 2, half * 2);
      }
    } else {
      const [r, g, b] = ITEM_RGB;
      const radius = Math.max(2, cell * 0.1) * marker.scale;
      ctx.fillStyle = `rgba(${r},${g},${b},0.85)`;
      ctx.beginPath();
      ctx.moveTo(marker.x, marker.y - radius);
      ctx.lineTo(marker.x + radius, marker.y);
      ctx.lineTo(marker.x, marker.y + radius);
      ctx.lineTo(marker.x - radius, marker.y);
      ctx.closePath();
      ctx.fill();
    }
  }
}

/** Her path up to the current frame: a fading trail, oldest segments faintest. */
function drawTrail(
  ctx: CanvasRenderingContext2D,
  replay: ReplayPayload,
  frameIndex: number,
  cell: number,
  agent: number,
): void {
  const width = replay.grid.width;
  const center = (node: number): [number, number] => [
    ((node % width) + 0.5 + jitterX(agent)) * cell,
    (Math.floor(node / width) + 0.5 + jitterY(agent)) * cell,
  ];
  ctx.lineWidth = 2;
  ctx.lineCap = "round";
  for (let i = 1; i <= frameIndex && i < replay.frames.length; i++) {
    const from = replay.frames[i - 1].positions[agent];
    const to = replay.frames[i].positions[agent];
    if (from === to) continue;
    const alpha = 0.08 + 0.45 * (i / Math.max(1, frameIndex));
    ctx.strokeStyle = `rgba(232,163,61,${alpha.toFixed(3)})`;
    const [fx, fy] = center(from);
    const [tx, ty] = center(to);
    ctx.beginPath();
    ctx.moveTo(fx, fy);
    ctx.lineTo(tx, ty);
    ctx.stroke();
  }
}

/** Draw one tick: field heat, world-component markers, agents colored by verb,
 *  corpses as crosses. `showWorld` gates the world layer (the toggle in ReplayView). */
export function drawFrame(
  ctx: CanvasRenderingContext2D,
  replay: ReplayPayload,
  frameIndex: number,
  cell: number,
  focus: number | null = null,
  showWorld = true,
): void {
  const { width, height } = replay.grid;
  const frame = replay.frames[frameIndex];
  if (!frame) return;
  ctx.fillStyle = "#0c0f15";
  ctx.fillRect(0, 0, width * cell, height * cell);

  replay.fields.forEach((name, index) => {
    const values = frame.fields[name];
    if (!values) return;
    const [r, g, b] = fieldColor(name, index);
    const nodes = Math.min(values.length, width * height);
    for (let node = 0; node < nodes; node++) {
      const value = Math.max(0, Math.min(1, values[node]));
      if (value <= 0.01) continue;
      ctx.fillStyle = `rgba(${r},${g},${b},${(0.6 * value).toFixed(3)})`;
      ctx.fillRect((node % width) * cell, Math.floor(node / width) * cell, cell, cell);
    }
  });

  // Subtle grid lines (one batched path — not one stroke per line).
  ctx.strokeStyle = "rgba(255,255,255,0.06)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let x = 0; x <= width; x++) {
    ctx.moveTo(x * cell + 0.5, 0);
    ctx.lineTo(x * cell + 0.5, height * cell);
  }
  for (let y = 0; y <= height; y++) {
    ctx.moveTo(0, y * cell + 0.5);
    ctx.lineTo(width * cell, y * cell + 0.5);
  }
  ctx.stroke();

  if (showWorld) {
    drawWorldMarkers(ctx, worldMarkers(replay, frameIndex, cell), cell);
  }

  if (focus !== null && focus < replay.population) {
    drawTrail(ctx, replay, frameIndex, cell, focus);
  }

  const verbOf = new Map<number, string>();
  for (const [actor, verb] of frame.actions) verbOf.set(actor, verb);
  const radius = Math.max(2.5, cell * 0.14);
  for (let agent = 0; agent < replay.population; agent++) {
    const node = frame.positions[agent];
    const x = ((node % width) + 0.5 + jitterX(agent)) * cell;
    const y = (Math.floor(node / width) + 0.5 + jitterY(agent)) * cell;
    const verb = verbOf.get(agent);
    const dimmed = focus !== null && agent !== focus;
    ctx.globalAlpha = dimmed ? 0.25 : 1;
    if (verb === undefined) {
      // The dead lie where they fell.
      ctx.strokeStyle = "#8b929e";
      ctx.lineWidth = 1.4;
      const arm = radius * 0.9;
      ctx.beginPath();
      ctx.moveTo(x - arm, y - arm);
      ctx.lineTo(x + arm, y + arm);
      ctx.moveTo(x - arm, y + arm);
      ctx.lineTo(x + arm, y - arm);
      ctx.stroke();
    } else {
      ctx.fillStyle = replay.palette[verb] ?? "#cccccc";
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = "rgba(0,0,0,0.55)";
      ctx.lineWidth = 1;
      ctx.stroke();
    }
    if (focus === agent) {
      ctx.strokeStyle = "#e8a33d";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(x, y, radius + 3, 0, Math.PI * 2);
      ctx.stroke();
    }
  }
  ctx.globalAlpha = 1;
}

/** The agent whose dot sits under (x, y) at this frame, or null.
 *  Nearest within 1.5 dot radii wins; ties go to the lower id. */
export function hitTestAgent(
  replay: ReplayPayload,
  frameIndex: number,
  cell: number,
  x: number,
  y: number,
): number | null {
  const frame = replay.frames[frameIndex];
  if (!frame) return null;
  const width = replay.grid.width;
  const radius = Math.max(2.5, cell * 0.14);
  const reach = radius * 1.5;
  let best: number | null = null;
  let bestDistance = Infinity;
  for (let agent = 0; agent < replay.population; agent++) {
    const node = frame.positions[agent];
    const ax = ((node % width) + 0.5 + jitterX(agent)) * cell;
    const ay = (Math.floor(node / width) + 0.5 + jitterY(agent)) * cell;
    const distance = Math.hypot(x - ax, y - ay);
    if (distance <= reach && distance < bestDistance) {
      bestDistance = distance;
      best = agent;
    }
  }
  return best;
}

/** A cell size that fits the grid into the host box (min 10, max 64 px). */
export function fitCell(
  hostWidth: number,
  hostHeight: number,
  gridWidth: number,
  gridHeight: number,
): number {
  const cell = Math.floor(Math.min(hostWidth / gridWidth, hostHeight / gridHeight));
  return Math.max(10, Math.min(64, cell));
}
